import { inject, injectable } from 'inversify'
import type { PaginationUrlFilterScalarValueType } from '@/services/tables/types'
import {
  WordsFilterOperation,
  type FilterToFormattedTextConvertable,
  type FormattedTextNode,
  type Operation,
  type FormattedTextBadgesValueItem,
} from '@/services/types'
import { SERVICE_TYPES } from '@/core/container/types'
import type TranslateService from '@/services/translateService'
import TmLogicError from '@/core/error/tmLogicError'
import { coersceBoolean } from '@/utils/boolean/coersceBoolean'
import { getFullFilledOperations } from '@/services/filters/getFullFilledOperations'

interface ParsedValue {
  value: string[]
  hasValue: boolean
}

const divider = ', '

@injectable()
export default class WordFilterMapper implements FilterToFormattedTextConvertable {
  constructor(@inject(SERVICE_TYPES.TranslateService) protected readonly translateService: TranslateService) {}

  public mapOperation(operation: Operation, value: unknown) {
    switch (operation) {
      case 'anyOf':
        return WordsFilterOperation.AnyOf
      case 'allOf':
        return WordsFilterOperation.AllOf
      case 'noneOf':
        return WordsFilterOperation.NoneOf
      case 'isEmpty':
        return coersceBoolean(value, WordsFilterOperation.IsNull, WordsFilterOperation.IsNotNull)
      default:
        throw new TmLogicError('Unknown operation')
    }
  }

  public parseValue(value: unknown, wordsFilterOperation: WordsFilterOperation): ParsedValue {
    if (
      wordsFilterOperation === WordsFilterOperation.IsNull ||
      wordsFilterOperation === WordsFilterOperation.IsNotNull
    ) {
      return {
        value: [],
        hasValue: true,
      }
    }
    return Array.isArray(value)
      ? {
          value,
          hasValue: value.length > 0,
        }
      : {
          value: [],
          hasValue: false,
        }
  }

  public getLabel(wordsFilterOperation: WordsFilterOperation, parsedValue: ParsedValue) {
    const operationLabelMap: Record<WordsFilterOperation, string> = {
      [WordsFilterOperation.IsNull]: this.translateService.t('tmFilterWords.options.isEmpty'),
      [WordsFilterOperation.IsNotNull]: this.translateService.t('tmFilterWords.options.isNotEmpty'),
      [WordsFilterOperation.NoneOf]: this.translateService.t('tmFilterWords.doesNotContain'),
      [WordsFilterOperation.AnyOf]: this.translateService.t('tmFilterWords.contains'),
      [WordsFilterOperation.AllOf]: this.translateService.t('tmFilterWords.containsAll'),
    }
    if (
      [WordsFilterOperation.AnyOf, WordsFilterOperation.AllOf, WordsFilterOperation.NoneOf].includes(
        wordsFilterOperation,
      )
    ) {
      return {
        op: operationLabelMap[wordsFilterOperation],
        main: parsedValue.value.length > 0 ? parsedValue.value.join(divider) : this.translateService.t('common.any'),
      }
    }
    return {
      op: operationLabelMap[wordsFilterOperation],
      main: '',
    }
  }

  public toFormattedText(filterValue: PaginationUrlFilterScalarValueType): FormattedTextNode[] {
    const operation = getFullFilledOperations(filterValue)[0]
    const value = filterValue[operation]
    const emailOperation = this.mapOperation(operation, value)
    const parsedValue = this.parseValue(value, emailOperation)
    const label = this.getLabel(emailOperation, parsedValue)

    const result: FormattedTextNode[] = []
    if (label.op) {
      result.push({ type: 'italic', value: label.op })
    }
    if (label.main) {
      result.push({ type: 'badges', value: this.getBadgesValueItemsFromLabel(label.main) })
    }
    return result
  }

  protected getBadgesValueItemsFromLabel(label: string): FormattedTextBadgesValueItem[] {
    return label.split(divider).map((part) => part)
  }
}
